import type { AxiosInstance, RawAxiosRequestHeaders } from "axios";
import type { Body, Config, HttpClient, Response, Header } from "./HttpClient";
import axios from "axios";
import axiosRetry from "axios-retry";
import { failedWithStatusCode } from "@core/shared/services/http/HttpError";

const TIMEOUT_IN_MILLIS = 60000;
const NO_BODY = undefined;

const containsAFile = (body: Body | undefined) : boolean => body instanceof FormData;

export class AxiosClient implements HttpClient {
  private axiosInstance : AxiosInstance;
  private readonly token: string;
  private readonly baseUrl: string;
  private readonly language?: string;

  constructor(token: string, baseUrl: string, language?: string) {
    this.token = token;
    this.language = language;
    this.baseUrl = baseUrl;
    this.axiosInstance = axios.create({
      baseURL: baseUrl,
      timeout: TIMEOUT_IN_MILLIS,
    });

    axiosRetry(this.axiosInstance, { retryDelay: axiosRetry.exponentialDelay, retries: 10, retryCondition: error => {
        return failedWithStatusCode(error, 429);
      } });
  }

  public getBaseUrl = () : string => {
    return this.baseUrl;
  };

  private getDefaultHeaders = (body: Body | undefined): Partial<RawAxiosRequestHeaders> => {
    const headers: Partial<RawAxiosRequestHeaders> =  {
      "authorization": `Bearer ${this.token}`,
      "Content-Type": "application/json",
    };

    if (this.language) {
      headers["Accept-Language"] = this.language;
    }

    if (containsAFile(body)) {
      headers["Content-Type"] = "multipart/form-data";
    }
    else {
      headers["Accept"] = "application/json";
    }

    return headers;
  };

  public getAuthorizationHeaders = () : Header => {
    return  {
      "authorization": `Bearer ${this.token}`
    };
  };

  public async get<TResponseData>({ endpoint, params, ...rest }: Config): Promise<Response<TResponseData>> {
    return this.axiosInstance.get(
      endpoint,
      { headers: this.getDefaultHeaders(NO_BODY), params, ...rest },
    );
  }

  public async post<TResponseData>({ body, params, endpoint, ...rest }: Config): Promise<Response<TResponseData>> {
    return this.axiosInstance.post(
      endpoint,
      body,
      { headers: this.getDefaultHeaders(body), signal: rest.abortController?.signal, params, ...rest }
    );
  }

  public async put<TResponseData>({ body, params, endpoint, ...rest }: Config): Promise<Response<TResponseData>> {
    return this.axiosInstance.put(endpoint, body, {
      headers: this.getDefaultHeaders(body),
      signal: rest.abortController?.signal,
      params, ...rest
    });
  }

  public async delete({ body, params, endpoint, ...rest }: Config): Promise<Response<never>> {
    return this.axiosInstance.delete(endpoint, {
      headers: this.getDefaultHeaders(NO_BODY),
      data: body,
      params, ...rest
    });
  }

  public async patch<TResponseData>({ body, params, endpoint, ...rest }: Config): Promise<Response<TResponseData>> {
    return this.axiosInstance.patch(endpoint, body, {
      headers: this.getDefaultHeaders(body),
      params, ...rest
    });
  }

  public async head({ params, endpoint, ...rest }: Config): Promise<Response<never>> {
    return this.axiosInstance.head(endpoint, {
      headers: this.getDefaultHeaders(NO_BODY),
      params
      , ...rest
    });
  }
}
